import { useState, useCallback } from "react";

interface Step {
  label: string;
  description: string;
}

interface ProfileData {
  profileName: string;
  collections: string[];
  includeHistory: boolean;
  systemInstruction: string;
}

export const useProfileWizard = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState("");

  const steps: Step[] = [
    { label: "Información Básica", description: "Configura el nombre del perfil" },
    { label: "Colecciones", description: "Selecciona las colecciones a utilizar" },
    { label: "Prompt", description: "Configura las Instrucciones del Modelo/Asistente" },
  ];

  const validateStep1 = useCallback((profileData: ProfileData): boolean => {
    return profileData.profileName.trim() !== "";
  }, []);

  const validateStep2 = useCallback((profileData: ProfileData): boolean => {
    return profileData.collections.length > 0;
  }, []);

  const validateCurrentStep = useCallback((profileData: ProfileData): boolean => {
    switch (activeStep) {
      case 0:
        return validateStep1(profileData);
      case 1:
        return validateStep2(profileData);
      case 2:
        return true;
      default:
        return true;
    }
  }, [activeStep, validateStep1, validateStep2]);

  const validateStepWithError = useCallback((profileData: ProfileData): boolean => {
    switch (activeStep) {
      case 0:
        if (!profileData.profileName.trim()) {
          setError("El nombre del perfil es obligatorio");
          return false;
        }
        break;
      case 1:
        if (profileData.collections.length === 0) {
          setError("Debe seleccionar al menos una colección");
          return false;
        }
        break;
      case 2:
        break;
      default:
        break;
    }
    setError("");
    return true;
  }, [activeStep]);

  const handleNext = useCallback((profileData: ProfileData, onComplete: () => void): boolean => {
    if (!validateStepWithError(profileData)) {
      return false;
    }
    
    if (activeStep === steps.length - 1) {
      if (!showPreview) {
        setShowPreview(true);
        return true;
      } else {
        onComplete();
        return true;
      }
    }
    
    setActiveStep(prev => prev + 1);
    return true;
  }, [activeStep, steps.length, showPreview, validateStepWithError]);

  const handleBack = useCallback((): void => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    setActiveStep(prev => Math.max(0, prev - 1));
  }, [showPreview]);

  const goToStep = useCallback((stepIndex: number): void => {
    if (stepIndex < activeStep) {
      setActiveStep(stepIndex);
      setShowPreview(false);
    }
  }, [activeStep]);

  const resetWizard = useCallback((): void => {
    setActiveStep(0);
    setShowPreview(false);
    setError("");
  }, []);

  const canGoNext = useCallback((profileData: ProfileData): boolean => {
    return validateCurrentStep(profileData);
  }, [validateCurrentStep]);

  const canGoBack = useCallback((): boolean => {
    return activeStep > 0 || showPreview;
  }, [activeStep, showPreview]);

  const isLastStep = useCallback((): boolean => {
    return activeStep === steps.length - 1;
  }, [activeStep, steps.length]);

  const getNextButtonText = useCallback((isLoading: boolean, editMode: boolean): string => {
    if (isLoading) {
      return "Procesando...";
    }
    
    if (isLastStep()) {
      if (showPreview) {
        return editMode ? "Actualizar Perfil" : "Crear Perfil";
      } else {
        return "Resumen de Perfil";
      }
    }
    
    return "Siguiente";
  }, [isLastStep, showPreview]);

  const getBackButtonText = useCallback((): string => {
    return showPreview ? "Editar" : "Atrás";
  }, [showPreview]);

  return {
    activeStep,
    showPreview,
    error,
    steps,
    
    setActiveStep,
    setShowPreview,
    setError,
    handleNext,
    handleBack,
    goToStep,
    resetWizard,
    
    validateStep1,
    validateStep2,
    validateCurrentStep,
    validateStepWithError,
    
    canGoNext,
    canGoBack,
    isLastStep,
    getNextButtonText,
    getBackButtonText
  };
};